import _has from 'lodash/has';
import type { SagaIterator } from 'redux-saga';
import {
  type ActionPattern,
  all,
  call,
  put,
  race,
  select,
  type TakeEffect,
  take,
  takeEvery,
  takeLatest
} from 'redux-saga/effects';
import type { ActionType } from 'typesafe-actions';
import { createUser, getUser, loginUser, logoutUser } from '../auth/actions.js';
import { getUserId } from '../auth/selectors.js';
import { getRoutineSaga } from '../common/utils/saga.js';
import { editingSession } from '../content/actions.js';
import { SET_CURRENT_PROJECT } from '../project/action-types.js';
import { setCurrentProject } from '../project/actions.js';
import { getCurrentProjectId } from '../project/selectors.js';
import type { Member } from '../project/types.js';
import { getProjectChannelName, pusherChannel } from '../pusher.js';
import { type ChannelInfo, getCollabUsers, initUsers, userEntered, userLeft } from './actions.js';
import { getCollabUsersQuery } from './api.js';
import type { User, UserId } from './types.js';

function mapUser(member: Member): User {
  // TODO fix type
  return { id: member.id, ...member.info } as unknown as User;
}

function* handleSubscriptionSucceeded(
  // TODO specify type
  members: any,
  channelInfo: ChannelInfo
): SagaIterator {
  const users: Record<UserId, User> = {};
  members.each((member: Member) => {
    if (member.id !== members.me.id) {
      users[member.id] = mapUser(member);
    }
  });

  yield put(initUsers({ channelInfo, users }));

  if (!_has(channelInfo, 'contentId')) {
    const projectId = yield select(getCurrentProjectId);
    yield put(getCollabUsers.requested({ projectId }));
  }
}

function* handleMemberAdded(member: Member, channelInfo: ChannelInfo) {
  yield put(userEntered({ channelInfo, user: mapUser(member) }));
}

function* handleMemberRemoved(member: Member, channelInfo: ChannelInfo) {
  yield put(userLeft({ channelInfo, user: mapUser(member) }));
}

function* handleServerAction(action: ActionType<unknown>) {
  yield put(action);
}

export const presenceEvents = {
  'pusher:subscription_succeeded': handleSubscriptionSucceeded,
  'pusher:member_added': handleMemberAdded,
  'pusher:member_removed': handleMemberRemoved,
  'server-action': handleServerAction
};

export const privateEvents = {
  'server-action': handleServerAction
};

function getUserChannelName(userId: string) {
  return `private-user.${userId}`;
}

export type GetContentChannelNameArgs = {
  projectId: string;
  contentId: string;
  shapeId: string;
};

function getContentChannelName({ projectId, contentId, shapeId }: GetContentChannelNameArgs): string {
  return `presence-content.${projectId}.${shapeId}.${encodeURIComponent(contentId)}`;
}

export type HandleEventsArgs = {
  channelName: string;
  channelInfo: { projectId: string };
  doneEffect: TakeEffect;
  events: any;
};

export function* handleEvents(context: unknown): SagaIterator {
  const { channelName, channelInfo, doneEffect, events } = context as HandleEventsArgs;
  const channel = yield call(pusherChannel, channelName, Object.keys(events));
  while (true) {
    const { event } = yield race({
      event: take(channel),
      done: doneEffect,
      logout: take(logoutUser.FULFILLED)
    });

    if (event) {
      yield call(events[event.type], event.payload, channelInfo);
    } else {
      channel.close();
      break;
    }
  }
}

export function* waitFor(actionName: ActionPattern, contentId: string): SagaIterator {
  while (true) {
    const { payload } = yield take(actionName);
    if (payload.contentId === contentId) {
      break;
    }
  }
}

export function* projectSaga({ payload }: ActionType<typeof setCurrentProject>): SagaIterator {
  if (payload.projectId) {
    yield call(handleEvents, {
      channelName: getProjectChannelName(payload.projectId),
      channelInfo: { projectId: payload.projectId },
      doneEffect: take(SET_CURRENT_PROJECT),
      events: presenceEvents
    });
  }
}

export function* contentSaga({ payload }: ActionType<typeof editingSession.requested>): SagaIterator {
  const projectId = yield select(getCurrentProjectId);
  const channelInfo = { ...payload, projectId };
  yield call(handleEvents, {
    channelName: getContentChannelName(channelInfo),
    channelInfo,
    doneEffect: call(waitFor, editingSession.cancelled, payload.contentId),
    events: presenceEvents
  });
}

export function* userSaga(): SagaIterator {
  const userId = yield select(getUserId);
  yield call(handleEvents, {
    channelName: getUserChannelName(userId),
    channelInfo: {},
    doneEffect: take([logoutUser.FULFILLED, getUser.FULFILLED]),
    events: privateEvents
  });
}

const getCollabUsersSaga = getRoutineSaga(getCollabUsers, getCollabUsersQuery);

export default function* root() {
  yield all([
    takeEvery(setCurrentProject, projectSaga),
    takeEvery(editingSession.requested, contentSaga),
    takeEvery([loginUser.FULFILLED, createUser.FULFILLED, getUser.FULFILLED], userSaga),
    takeLatest(getCollabUsers.REQUESTED, getCollabUsersSaga)
  ]);
}
